import React, { useState } from "react";
import { createPortal } from "react-dom";

import {
  closestCenter,
  CollisionDetection,
  DragOverlay,
  DndContext,
  DropAnimation,
  KeyboardSensor,
  KeyboardCoordinateGetter,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
  defaultDropAnimationSideEffects,
} from "@dnd-kit/core";
import {
  arrayMove,
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  SortingStrategy,
  rectSortingStrategy,
} from "@dnd-kit/sortable";

import { Item, List, Wrapper } from ".";
import { OptionItems } from "../SurveySlice";

export interface Props {
  adjustScale?: boolean;
  collisionDetection?: CollisionDetection;
  coordinateGetter?: KeyboardCoordinateGetter;
  Container?: any;
  dropAnimation?: DropAnimation | null;
  handle?: boolean;
  selectItems?: Array<OptionItems>;
  renderContent?(item: OptionItems, index: number): React.ReactNode;
  reorderItems?: typeof arrayMove;
  strategy?: SortingStrategy;
  useDragOverlay?: boolean;
  onReorder?: (items: Array<OptionItems>) => void;
}

const dropAnimationConfig: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: "0.5",
      },
    },
  }),
};

export function Sortable({
  adjustScale = false,
  Container = List,
  collisionDetection = closestCenter,
  coordinateGetter = sortableKeyboardCoordinates,
  dropAnimation = dropAnimationConfig,
  handle = false,
  selectItems = [],
  renderContent,
  reorderItems = arrayMove,
  strategy = rectSortingStrategy,
  useDragOverlay = true,
  onReorder,
}: Props) {
  const itemIds: UniqueIdentifier[] = selectItems.map(
    (selectItem) => selectItem.dndId || selectItem.name
  );
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      // Disable smooth scrolling in Cypress automated tests
      scrollBehavior: "Cypress" in window ? "auto" : undefined,
      coordinateGetter,
    })
  );
  const getIndex = (id: UniqueIdentifier) => itemIds.indexOf(id);
  const activeIndex = activeId ? getIndex(activeId) : -1;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={({ active }) => {
        if (!active) {
          return;
        }

        setActiveId(active.id);
      }}
      onDragEnd={({ over }) => {
        setActiveId(null);

        if (over) {
          const overIndex = getIndex(over.id);
          if (activeIndex !== overIndex && onReorder) {
            const reorderedItems = reorderItems(
              selectItems,
              activeIndex,
              overIndex
            );
            onReorder(reorderedItems);
          }
        }
      }}
      onDragCancel={() => setActiveId(null)}
    >
      <Wrapper center>
        <SortableContext items={itemIds} strategy={strategy}>
          <Container>
            {itemIds.map((value, index) => (
              <SortableItem
                key={value}
                id={value}
                handle={handle}
                index={index}
                renderContent={renderContent}
                selectItems={selectItems}
                useDragOverlay={useDragOverlay}
              />
            ))}
          </Container>
        </SortableContext>
      </Wrapper>
      {useDragOverlay
        ? createPortal(
            <DragOverlay
              adjustScale={adjustScale}
              dropAnimation={dropAnimation}
              style={{ zIndex: 1500 }}
            >
              {activeId ? (
                <Item value={itemIds[activeIndex]} handle={handle} dragOverlay>
                  {renderContent && selectItems[activeIndex]
                    ? renderContent(selectItems[activeIndex], activeIndex)
                    : null}
                </Item>
              ) : null}
            </DragOverlay>,
            document.body
          )
        : null}
    </DndContext>
  );
}

interface SortableItemProps {
  id: UniqueIdentifier;
  index: number;
  handle: boolean;
  useDragOverlay?: boolean;
  renderContent?(item: OptionItems, index: number): React.ReactNode;
  selectItems?: Array<OptionItems>;
}

export function SortableItem({
  handle,
  id,
  index,
  renderContent,
  selectItems,
  useDragOverlay,
}: SortableItemProps) {
  const {
    attributes,
    isDragging,
    isSorting,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({
    id,
  });
  // renderContentが指定されている場合、該当するitemを取得
  const currentItem = selectItems?.find(
    (item) => (item.dndId || item.name) === id
  );

  return (
    <Item
      ref={setNodeRef}
      value={id}
      dragging={isDragging}
      sorting={isSorting}
      handle={handle}
      handleProps={
        handle
          ? {
              ref: setActivatorNodeRef,
            }
          : undefined
      }
      index={index}
      transform={transform}
      transition={transition}
      listeners={listeners}
      dragOverlay={!useDragOverlay && isDragging}
      {...attributes}
    >
      {renderContent && currentItem && index !== undefined
        ? renderContent(currentItem, index)
        : null}
    </Item>
  );
}
